import { push } from "connected-react-router";
import { call, fork, put, take } from "redux-saga/effects";
import { Client } from "../../domain/model/client";

import {
  IClientRepository,
  TCreateClient,
  TUpdateClientRole,
} from "../../domain/repository/client_repository";

import { createApiClient, IApiClient } from "../../infra/api/client";

import {
  ClientClientsActionType,
  createClientClientsActionCreator,
  IClientClientsActionCreator,
  IRequestCreateClientAction,
  IRequestGetAllClientsAction,
  IRequestUpdateRoleClientAction,
} from "../action/client_clients_action";

const apiClient: IApiClient = createApiClient();
const clientRepository: IClientRepository = createClientAPI(apiClient);
const actionCreator: IClientClientsActionCreator =
  createClientClientsActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

import {
  CreateClientByClientResponse,
  DeleteClientByClientResponse,
  GetAllClientsByClientResponse,
  UpdateRoleClientByClientResponse,
} from "../response/clients_response";

import { NotificationClient } from "../../config/view_const/notification";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import { handleErrorForHandler } from "./handleErrorForHandler";

import { setSubmitSucceeded, startSubmit, stopSubmit } from "redux-form";
import { Pager } from "../../domain/model/common";
import { createClientAPI } from "../../infra/api/service/client_api";

function* handleGetAllClientsInClientClients() {
  while (true) {
    try {
      const action: IRequestGetAllClientsAction = yield take(
        ClientClientsActionType.REQUEST_GET_ALL_CLIENTS
      );
      const clients: Client[] = yield call(getAllClients, action.item.pager);
      const res: GetAllClientsByClientResponse =
        new GetAllClientsByClientResponse();
      res.clients = clients;
      yield put(actionCreator.callbackGetAllClientsAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackGetAllClientsAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleCreateClientInClientClients() {
  while (true) {
    const formName: string = "ClientClientsCreateForm";
    try {
      const action: IRequestCreateClientAction = yield take(
        ClientClientsActionType.REQUEST_CREATE_CLIENT
      );
      yield put(startSubmit(formName));
      yield call(createClient, action.item);
      const clients: Client[] = yield call(getAllClients, action.item.pager);
      const res: CreateClientByClientResponse =
        new CreateClientByClientResponse();
      res.clients = clients;
      yield put(actionCreator.callbackCreateClientAction(true, res));
      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationClient.successCreateClient;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
      yield put(stopSubmit(formName));
      yield put(setSubmitSucceeded(formName));
      yield put(push(`/client/clients`));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(stopSubmit(formName, { _error: error.toString() }));
      yield put(
        actionCreator.callbackCreateClientAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleUpdateRoleClientInClientClients() {
  while (true) {
    try {
      const action: IRequestUpdateRoleClientAction = yield take(
        ClientClientsActionType.REQUEST_UPDATE_ROLE_CLIENT
      );
      yield call(updateRoleClient, {
        clientID: action.item.clientId,
        role: action.item.role,
      });
      const clients: Client[] = yield call(getAllClients, action.item.pager);
      const res: UpdateRoleClientByClientResponse =
        new UpdateRoleClientByClientResponse();
      res.clients = clients;
      yield put(actionCreator.callbackUpdateRoleClientAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackUpdateRoleClientAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleDeleteClientInClientClients() {
  while (true) {
    try {
      const action: IRequestUpdateRoleClientAction = yield take(
        ClientClientsActionType.REQUEST_DELETE_CLIENT
      );
      yield call(deleteClient, action.item.clientId);
      const clients: Client[] = yield call(getAllClients, action.item.pager);
      const res: DeleteClientByClientResponse =
        new DeleteClientByClientResponse();
      res.clients = clients;
      yield put(actionCreator.callbackDeleteClientAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackDeleteClientAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

const getAllClients = (pager: Pager): Promise<Client[]> => {
  return clientRepository.getAll(pager.page, pager.offset);
};

const createClient = (item: TCreateClient): Promise<Client> => {
  return clientRepository.create(item);
};

const updateRoleClient = (item: TUpdateClientRole): Promise<void> => {
  return clientRepository.updateRole(item);
};

const deleteClient = (clientId: string): Promise<void> => {
  return clientRepository.delete(clientId);
};

export {
  handleCreateClientInClientClients,
  handleDeleteClientInClientClients,
  handleGetAllClientsInClientClients,
  handleUpdateRoleClientInClientClients,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_clients_handler.ts